import {Mappings} from '@/engine/types/renderer/pdf';
import {SupportedLocale} from '@/initializers/i18next';
import {YesNoChoice} from '@/model/src/values/dv-3';
import {WithdrawalTypeCIQ} from '@/model/src/values/dv-72';
import {ContractTypes} from '@/model/src/values/dv-93';
import {DocumentValue, ValueResolver, VisibilitySet} from '../../types';

function resolveRef(valueResolver: ValueResolver, ref: string): string {
  const resolved = valueResolver({$ref: ref}, {keepDomainReference: true}) as DocumentValue | DocumentValue[];
  return typeof resolved === 'object' && '$ref' in resolved ? resolved.$ref : '';
}

function mapExistingAccountCodesForT09x(
  output: Mappings,
  valueResolver: ValueResolver,
  visibilitySet: VisibilitySet,
): void {
  const isPreAuthorizedTransferProgram = resolveRef(valueResolver, 'PTP');

  if (isPreAuthorizedTransferProgram !== YesNoChoice.NO) return;

  const contractType = resolveRef(valueResolver, 'C14');

  if (contractType === ContractTypes.CI) {
    const dailyInterestAccountsCount = parseInt(valueResolver({$ref: '_I_dailyInterestAccounts[count]'}));
    const guaranteedInterestAccountsCount = parseInt(valueResolver({$ref: '_I_guaranteedInterestAccounts[count]'}));
    const benevaInvestmentAccountsCount = parseInt(valueResolver({$ref: '_I_benevaInvestmentAccounts[count]'}));

    const codes: string[] = [];
    for (let i = 0; i < dailyInterestAccountsCount; ++i) {
      codes.push('CIQ');
    }
    for (let i = 0; i < guaranteedInterestAccountsCount; ++i) {
      const code = valueResolver({$ref: `I13_1#${i}`}, {visibilitySet});
      if (code) codes.push(code);
    }
    for (let i = 0; i < benevaInvestmentAccountsCount; ++i) {
      const code = valueResolver({$ref: `I151_2#${i}`}, {visibilitySet});
      if (code) codes.push(code);
    }

    codes.forEach((code, index) => (output[`_I13_1-${index}|I151_2-${index}|CIQ-${index}`] = code));
  } else {
    const highYieldAccountsCount = parseInt(valueResolver({$ref: '_I_highYieldAccounts[count]'}));
    const guaranteedInterestAccountsCount = parseInt(valueResolver({$ref: '_I_guaranteedInterestAccounts[count]'}));
    const benevaGuaranteedInvestmentFundsCount = parseInt(
      valueResolver({$ref: '_I_benevaGuaranteedInvestmentFunds[count]'}),
    );

    const codes: string[] = [];
    for (let i = 0; i < highYieldAccountsCount; ++i) {
      codes.push('CHR');
    }
    for (let i = 0; i < guaranteedInterestAccountsCount; ++i) {
      const code = valueResolver({$ref: `I13_1#${i}`}, {visibilitySet});
      if (code) codes.push(code);
    }
    for (let i = 0; i < benevaGuaranteedInvestmentFundsCount; ++i) {
      const code = valueResolver({$ref: `I187#${i}`}, {visibilitySet});
      if (code) codes.push(code);
    }

    codes.forEach((code, index) => (output[`_I13_1-${index}|I187_2-${index}|CHR-${index}`] = code));
  }
}

function mapOtherInvestmentsSectionForFHSA(
  output: Mappings,
  valueResolver: ValueResolver,
  visibilitySet: VisibilitySet,
  locale: SupportedLocale,
): void {
  const i193 = resolveRef(valueResolver, 'I193');
  const c1 = resolveRef(valueResolver, 'C1');

  if (!i193 || !c1 || i193 === c1) return;

  const highYieldCount = parseInt(valueResolver({$ref: '_I_highYieldAccounts[count]'})) || 0;
  const guaranteedFundsCount = parseInt(valueResolver({$ref: '_I_benevaGuaranteedInvestmentFunds[count]'})) || 0;

  const suffix = locale === 'fr' ? 'CHR' : 'HYA';
  let pdfIndex = 0;

  // This merges the two arrays of accounts into a single list of 10 rows, filling in the PDF fields with the appropriate values.
  // The high yield accounts use their actual indexes, while the guaranteed funds are appended after the high yield accounts.
  for (let i = 0; i < highYieldCount && pdfIndex < 10; ++i, ++pdfIndex) {
    output[`_I186-${pdfIndex}|${suffix}-${pdfIndex}`] = suffix;
    output[`_I187-${pdfIndex}`] = '';
    output[`_I103_4-${pdfIndex}_dv72_0|I103_3-${pdfIndex}_dv72_0`] =
      resolveRef(valueResolver, `I103_3#${i}`) === WithdrawalTypeCIQ.TOTAL;
    output[`_I11_4-${pdfIndex}|I11_3-${pdfIndex}`] = valueResolver({$ref: `I11_3#${i}`}, {visibilitySet}) || '';
  }

  for (let i = 0; i < guaranteedFundsCount && pdfIndex < 10; ++i, ++pdfIndex) {
    output[`_I186-${pdfIndex}|${suffix}-${pdfIndex}`] = valueResolver({$ref: `I186#${i}`}, {visibilitySet}) || '';
    output[`_I187-${pdfIndex}`] = valueResolver({$ref: `I187#${i}`}, {visibilitySet}) || '';
    output[`_I103_4-${pdfIndex}_dv72_0|I103_3-${pdfIndex}_dv72_0`] =
      resolveRef(valueResolver, `I103_4#${i}`) === WithdrawalTypeCIQ.TOTAL;
    output[`_I11_4-${pdfIndex}|I11_3-${pdfIndex}`] = valueResolver({$ref: `I11_4#${i}`}, {visibilitySet}) || '';
  }
}

/*
  This function adds custom mappings that can't be expressed with the default PDF mappings (DSL).
  Fields prefixed with underscore (_) are computed here and injected after DSL resolution.
*/
export function addCustomMappings(
  input: Mappings,
  valueResolver: ValueResolver,
  visibilitySet: VisibilitySet,
  locale: SupportedLocale,
): Mappings {
  const output: Mappings = {...input};

  mapExistingAccountCodesForT09x(output, valueResolver, visibilitySet);
  mapOtherInvestmentsSectionForFHSA(output, valueResolver, visibilitySet, locale);

  return output;
}
